import React, { useEffect, useState } from 'react';
import { FaSignInAlt } from 'react-icons/fa';

const instructionTopics = {
  reservation: {
    label: 'Paikan varaaminen',
    title: 'Ohje: Paikan varaaminen',
    content: (
      <ol className="list-decimal space-y-2 pl-5 text-gray-800">
        <li>
          Kirjaudu sisään tai rekisteröidy yläpalkin kirjautumispainikkeesta{' '}
          <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-1 text-slate-700 align-middle">
            <FaSignInAlt className="mr-1" />
          </span>
          .
        </li>
        <li>Siirry sivulle <strong>Myyjille -&gt; Myyntipaikat</strong>.</li>
        <li>Valitse haluamasi vapaa myyntipaikka kartalta klikkaamalla paikkaa kartalla.</li>
        <li>Vahvista varaus painamalla <strong>Vahvista...</strong> painiketta.</li>
        <li>Valitse tarvitsetko valitsemallesi paikalle myös sähköpisteen ja jatka painamalla <strong>Seuraava</strong>.</li>
        <li>Ilmoita laskutus ja myyjätiedot valitsemalla myyjä listalta tai painamalla <strong>Lisää uusi</strong> mikäli sinulle ei vielä ole myyjäprofiilia.</li>
        <li>Täytä tarvittavat tiedot huolellisesti ja tarkista yhteystiedot.</li>
        <li>Hyväksy <strong>Olen lukenut ja hyväksyn Turvallisuusohjeet</strong> ja tee varaus painamalla <strong>Vahvista varaus</strong> painiketta.</li>
        <li>Varausvahvistus ja lasku lähetetään laskutussähköpostiin ja laskun voi myös ladata suoraan vahvistussivulta.</li>
      </ol>
    ),
  },
  password: {
    label: 'Salasanan vaihto',
    title: 'Ohje: Salasanan vaihto',
    content: (
      <ol className="list-decimal space-y-2 pl-5 text-gray-800">
        <li>Avaa yläpalkin profiilivalikko ja valitse <strong>Profiilini</strong>.</li>
        <li>Paina <strong>Vaihda salasana</strong> -painiketta.</li>
        <li>Syötä uusi salasana ja kirjoita se uudelleen vahvistuskenttään.</li>
        <li>Salasanan minimipituus on <strong>8 merkkiä</strong>.</li>
        <li>Paina <strong>Tallenna</strong>, kun salasanat täsmäävät.</li>
      </ol>
    ),
  },
  profile: {
    label: 'Tietojen muokkaus',
    title: 'Ohje: Tietojen muokkaus',
    content: (
      <ol className="list-decimal space-y-2 pl-5 text-gray-800">
        <li>Avaa yläpalkin profiilivalikko ja siirry sivulle <strong>Profiilini</strong>.</li>
        <li>Paina <strong>Muokkaa</strong> käyttäjätietojen kohdalla.</li>
        <li>Voit päivittää <strong>nimen</strong>, <strong>puhelinnumeron</strong> ja <strong>sähköpostin</strong>.</li>
        <li>Tallenna muutokset painamalla <strong>Tallenna</strong>.</li>
      </ol>
    ),
  },
  sellers: {
    label: 'Myyjälistaus',
    title: 'Ohje: Myyjälistaus',
    content: (
      <div className="space-y-2 text-gray-800">
        <p>Myyjälistauksessa voit selata mukana olevia myyjiä ja tarkistaa heidän tietonsa.</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Avaa <strong>myyjäsivu</strong> tai kartalta myyjän tiedot.</li>
          <li>Katso yhteystiedot, kuvaus ja mahdolliset julkiset linkit.</li>
          <li>Voit vertailla tarjontaa ja etsiä kiinnostavat myyjät helposti.</li>
        </ul>
      </div>
    ),
  },
  map: {
    label: 'Kartan käyttö',
    title: 'Ohje: Kartan käyttö',
    content: (
      <div className="space-y-2 text-gray-800">
        <p>Kartan avulla löydät myyntipaikat nopeasti ja näet niiden tilanteen.</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Zoomaa ja siirrä karttaa nähdäksesi eri alueet tarkemmin.</li>
          <li>Valitse paikka kartalta nähdäksesi lisätiedot.</li>
          <li>Vapaat paikat voit varata suoraan karttanäkymästä.</li>
        </ul>
      </div>
    ),
  },
};

const InstructionsModal = ({ open, onClose }) => {
  const [selectedTopic, setSelectedTopic] = useState('reservation');

  useEffect(() => {
    if (open) {
      setSelectedTopic('reservation');
    }
  }, [open]);

  if (!open) return null;

  const currentInstruction = instructionTopics[selectedTopic];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div
        className="relative w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl sm:p-8"
        style={{
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <button
          className="absolute right-3 top-2 text-2xl font-bold text-gray-500 hover:text-gray-800"
          onClick={onClose}
          aria-label="Sulje"
        >
          &times;
        </button>

        <div className="mb-5">
          <label htmlFor="instruction-topic" className="mb-2 block text-sm font-semibold text-gray-700">
            Valitse ohje
          </label>
          <select
            id="instruction-topic"
            value={selectedTopic}
            onChange={(event) => setSelectedTopic(event.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-blue-500 focus:outline-none"
          >
            {Object.entries(instructionTopics).map(([key, topic]) => (
              <option key={key} value={key}>
                {topic.label}
              </option>
            ))}
          </select>
        </div>

        <h2 className="mb-4 text-2xl font-bold text-blue-800">{currentInstruction.title}</h2>

        <div className="rounded-xl bg-slate-50 p-4 text-base leading-7">
          {currentInstruction.content}
        </div>

        <div className="mt-6 text-sm text-gray-600">
          Mikäli kohtaat ongelmia, ota yhteyttä{' '}
          <a href="mailto:laurinmarkkinat@lcmynamaki.fi" className="text-blue-500 underline underline-offset-2">
            laurinmarkkinat@lcmynamaki.fi
          </a>
        </div>
      </div>
    </div>
  );
};

export default InstructionsModal;
